import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  Input,
  Button,
  IconButton,
} from '@mui/joy';
import {
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Pencil,
} from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { isSuperadmin } from '../../utils/permissions';

export default function Organizzazioni() {
  const [orgs, setOrgs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrganizzazioni();
  }, []);

  const fetchOrganizzazioni = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_SERVER_HOST}/org`, {
        credentials: 'include',
      });

      if (!res.ok) throw new Error('Errore nel fetch');

      const data = await res.json();
      if (Array.isArray(data)) {
        setOrgs(data);
      } else {
        toast.error('Formato risposta non valido');
      }
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento delle organizzazioni');
    }
  };

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return orgs.filter(o => o.name.toLowerCase().includes(term));
  }, [orgs, searchTerm]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortConfig]);

  const paginated = useMemo(() => {
    const start = (page - 1) * perPage;
    return sorted.slice(start, start + perPage);
  }, [sorted, page]);

  const totalPages = Math.ceil(sorted.length / perPage);

  const handleSort = (key) => {
    setPage(1); 
    setSortConfig((curr) =>
      curr.key === key
        ? { key, direction: curr.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowDown size={14} color="#bbb" />;
    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={14} />
    ) : (
      <ArrowDown size={14} />
    );
  };

  return (
    <Box>
      <Typography level="h3" mb={2}>Organizzazioni</Typography>

      <Box
  mb={2}
  display="flex"
  alignItems="center"
  justifyContent="space-between"
  flexWrap="wrap"
  gap={2}
>
  <Box display="flex" alignItems="center" gap={2}>
    <Input
      placeholder="Cerca organizzazione..."
      value={searchTerm}
      onChange={(e) => {
        setSearchTerm(e.target.value);
        setPage(1);
      }}
      sx={{ maxWidth: 300 }}
    />
    <Typography level="body-sm" color="neutral">
      {sorted.length} record trovati
    </Typography>
  </Box>

  {isSuperadmin && (
    <Button
      variant="solid"
      color="primary"
      onClick={() => navigate('/org/nuova')}
    >
      + Nuova organizzazione
    </Button>
  )}
</Box>


      <Box
        sx={{
          borderRadius: 'lg',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'neutral.outlinedBorder',
        }}
      >
        <Table
          size="sm"
          sx={{
            minWidth: { xs: '100%', sm: 500 },
            borderRadius: 0,
            tableLayout: 'auto',
            '& thead th': {
              bgcolor: 'neutral.softBg',
              textAlign: 'left',
              px: { xs: 1, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              fontSize: { xs: 'xs', sm: 'sm' },
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            },
            '& tbody td': {
              px: { xs: 1, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              fontSize: { xs: 'sm', sm: 'md' },
              lineHeight: 1.2,
              wordBreak: 'break-word',
            },
            '& tbody tr:hover': {
              bgcolor: 'neutral.plainHoverBg',
            },
          }}
        >
          <thead>
            <tr>
              <th onClick={() => handleSort('id')}>
                <Box display="flex" alignItems="center" gap={1}>
                  ID
                  <SortIcon columnKey="id" />
                </Box>
              </th>
              <th onClick={() => handleSort('name')}>
                <Box display="flex" alignItems="center" gap={1}>
                  Nome
                  <SortIcon columnKey="name" />
                </Box>
              </th>
              <th>Stato</th>
              <th>Veicoli</th>
              <th>Licenza</th>
              <th style={{ textAlign: 'center', width: 80 }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length > 0 ? (
              paginated.map((org) => (
                <tr key={org.id}>
                  <td>{org.id}</td>
                  <td>{org.name}</td>
                  <td>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: org.status === 1 ? 'success.solidBg' : 'danger.solidBg',
                        display: 'inline-block',
                        mr: 1,
                      }}
                    />
                    {org.status === 1 ? 'Attivo' : 'Inattivo'}
                  </td>
                  <td>{org.vehicles}</td>
                  <td>{org.licence}</td>
                  <td style={{ textAlign: 'center' }}>
                    <IconButton
                      size="sm"
                      variant="soft"
                      color="primary"
                      onClick={() => navigate(`/org/${org.id}`)}
                    >
                      <Pencil size={16} />
                    </IconButton>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '12px' }}>
                  Nessuna organizzazione trovata.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Button
            size="sm"
            variant="outlined"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            startDecorator={<ChevronLeft size={14} />}
          >
            Precedente
          </Button>

          <Typography level="body-sm">Pagina {page} di {totalPages}</Typography>

          <Button
            size="sm"
            variant="outlined"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            endDecorator={<ChevronRight size={14} />}
          >
            Successiva
          </Button>
        </Box>
      )}
    </Box>
  );
}
